/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronUp, Search, RefreshCw, Plus, Download, Users, BarChart3 } from 'lucide-react';
import axios from 'axios';
import './Assessment.css';

const API = process.env.REACT_APP_BACKEND_URL;
const ADMIN_KEY = 'spring-admin-2026';

const PW_COLORS = {
  pathway_a: '#1E491A',
  pathway_b: '#1E491A',
  pathway_c: '#1E491A',
};
const PW_NAMES = {
  pathway_a: 'Founders Pathway',
  pathway_b: 'Employment Pathway',
  pathway_c: 'Independent Pathway',
};

/* ---- Pilot Code Generator ---- */
function PilotCodesPanel() {
  const [codes, setCodes] = useState([]);
  const [genCount, setGenCount] = useState(10);
  const [genWave, setGenWave] = useState(1);
  const [loading, setLoading] = useState(false);
  const [funnel, setFunnel] = useState(null);
  const [participants, setParticipants] = useState([]);

  const headers = { 'X-Admin-Key': ADMIN_KEY };

  const loadCodes = async () => {
    const res = await axios.get(`${API}/api/pilot/admin/codes`, { headers });
    setCodes(res.data.codes || []);
  };
  const loadFunnel = async () => {
    const res = await axios.get(`${API}/api/pilot/admin/funnel`, { headers });
    setFunnel(res.data.funnel);
  };
  const loadParticipants = async () => {
    const res = await axios.get(`${API}/api/pilot/admin/participants`, { headers });
    setParticipants(res.data.participants || []);
  };

  useEffect(() => { loadCodes(); loadFunnel(); loadParticipants(); }, []);

  const generateCodes = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/pilot/admin/codes/generate`,
        { count: genCount, wave: genWave, cap: 200 }, { headers });
      await loadCodes();
      await loadFunnel();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const downloadCSV = () => {
    window.open(`${API}/api/pilot/admin/csv?x_admin_key=${ADMIN_KEY}`, '_blank');
  };

  return (
    <div>
      {/* Funnel */}
      {funnel && (
        <div className="admin-stats" style={{ marginBottom: 24 }}>
          <div className="admin-stat"><span className="admin-stat-num">{funnel.codes_issued}</span><span className="admin-stat-label">Codes issued</span></div>
          <div className="admin-stat"><span className="admin-stat-num">{funnel.codes_used}</span><span className="admin-stat-label">Codes used</span></div>
          <div className="admin-stat"><span className="admin-stat-num">{funnel.registered}</span><span className="admin-stat-label">Registered</span></div>
          <div className="admin-stat"><span className="admin-stat-num">{funnel.started}</span><span className="admin-stat-label">Started</span></div>
          <div className="admin-stat"><span className="admin-stat-num">{funnel.completed}</span><span className="admin-stat-label">Completed</span></div>
          <div className="admin-stat"><span className="admin-stat-num">{funnel.feedback_submitted}</span><span className="admin-stat-label">Feedback</span></div>
        </div>
      )}

      {/* Code generation */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24, flexWrap: 'wrap' }}>
        <input type="number" value={genCount} onChange={e => setGenCount(+e.target.value)} min={1} max={50}
          style={{ width: 80, padding: '8px 12px', border: '2px solid #e5e5e5', borderRadius: 0 }} />
        <span style={{ fontSize: 14 }}>codes for Wave</span>
        <input type="number" value={genWave} onChange={e => setGenWave(+e.target.value)} min={1} max={10}
          style={{ width: 60, padding: '8px 12px', border: '2px solid #e5e5e5', borderRadius: 0 }} />
        <button className="assess-btn-primary" onClick={generateCodes} disabled={loading} style={{ marginTop: 0 }}
          data-testid="generate-codes-btn">
          <Plus size={16} /> {loading ? 'Generating...' : 'Generate'}
        </button>
        <button className="assess-btn-secondary" onClick={downloadCSV} data-testid="download-csv-btn">
          <Download size={16} /> Export CSV
        </button>
      </div>

      {/* Participants */}
      <h3 style={{ fontSize: 18, marginBottom: 12, color: '#1E491A' }}>Participants ({participants.length})</h3>
      <div style={{ overflowX: 'auto' }}>
      <table className="admin-table" data-testid="pilot-participants-table">
        <thead>
          <tr>
            <th>Ref</th><th>Name</th><th>Email</th><th>Wave</th><th>Situation</th>
            <th>Branch</th><th>Pathway</th><th>Scores</th><th>Satisfaction</th><th>Feedback</th><th>Flags</th>
          </tr>
        </thead>
        <tbody>
          {participants.map(p => (
            <tr key={p.id}>
              <td><strong>{p.ref}</strong></td>
              <td>{p.first_name} {p.last_name}</td>
              <td>{p.email}</td>
              <td>W{p.wave}</td>
              <td style={{ fontSize: 12, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.situation}</td>
              <td><span className={`admin-status ${p.branch || 'draft'}`}>{p.branch || '—'}</span></td>
              <td style={{ color: PW_COLORS[p.pathway] }}>{PW_NAMES[p.pathway] || '—'}</td>
              <td style={{ fontSize: 12 }}>
                {p.scores ? (
                  <span title={`Drive: ${p.scores.drive}, Durability: ${p.scores.durability}, Skills: ${p.scores.skills}`}>
                    {p.scores.total || '—'}
                  </span>
                ) : '—'}
              </td>
              <td>
                {p.satisfaction_accuracy ? (
                  <span className={`admin-status ${p.satisfaction_accuracy === 'Yes' ? 'completed' : p.satisfaction_accuracy === 'Mostly' ? 'draft' : 'failed'}`}
                    title={p.satisfaction_note || ''}>
                    {p.satisfaction_accuracy}
                  </span>
                ) : '—'}
              </td>
              <td>{p.feedback_submitted ? 'Yes' : '—'}</td>
              <td>{(p.flags || []).join(', ') || '—'}</td>
            </tr>
          ))}
          {participants.length === 0 && <tr><td colSpan={11} style={{ textAlign: 'center', color: '#999' }}>No participants yet</td></tr>}
        </tbody>
      </table>
      </div>

      {/* Codes list */}
      <h3 style={{ fontSize: 18, marginTop: 32, marginBottom: 12, color: '#1E491A' }}>
        Invitation Codes ({codes.length}) — {codes.filter(c => c.status === 'unused').length} unused
      </h3>
      <div style={{ maxHeight: 300, overflow: 'auto' }}>
        <table className="admin-table" data-testid="pilot-codes-table">
          <thead><tr><th>Code</th><th>Wave</th><th>Status</th><th>Used by</th></tr></thead>
          <tbody>
            {codes.map(c => (
              <tr key={c.id}>
                <td><code>{c.code}</code></td>
                <td>W{c.wave}</td>
                <td><span className={`admin-status ${c.status === 'used' ? 'completed' : 'draft'}`}>{c.status}</span></td>
                <td>{c.used_by || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminAssessments() {
  const [authed, setAuthed] = useState(false);
  const [pin, setPin] = useState('');
  const [tab, setTab] = useState('pilot'); // pilot | assessments
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);

  const handleAuth = (e) => {
    e.preventDefault();
    if (pin === ADMIN_KEY) {
      setAuthed(true);
    } else {
      alert('Invalid admin key');
    }
  };

  const loadAttempts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/assessments`, {
        headers: { 'X-Admin-Key': ADMIN_KEY },
      });
      setAttempts(res.data.attempts || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) loadAttempts();
  }, [authed, loadAttempts]);

  const loadDetail = async (attemptId) => {
    if (expanded === attemptId) {
      setExpanded(null);
      setDetail(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/api/admin/assessments/${attemptId}`, {
        headers: { 'X-Admin-Key': ADMIN_KEY },
      });
      setDetail(res.data);
      setExpanded(attemptId);
    } catch (err) {
      console.error(err);
    }
  };

  const filtered = attempts.filter(a =>
    !search || a.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (!authed) {
    return (
      <div className="admin-login-page">
        <form onSubmit={handleAuth} className="admin-login-form">
          <h2>Admin Access</h2>
          <input type="password" value={pin} onChange={e => setPin(e.target.value)}
            placeholder="Admin key" autoFocus data-testid="admin-key-input" />
          <button type="submit" className="assess-btn-primary" data-testid="admin-login-btn">Enter</button>
        </form>
      </div>
    );
  }

  return (
    <div className="admin-page" data-testid="admin-assessments">
      <div className="admin-inner">
        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid #e5e5e5' }}>
          <button onClick={() => setTab('pilot')}
            style={{ padding: '12px 24px', fontSize: 15, fontWeight: tab === 'pilot' ? 700 : 400, background: 'none', border: 'none',
              borderBottom: tab === 'pilot' ? '3px solid #1E491A' : '3px solid transparent', color: tab === 'pilot' ? '#1E491A' : '#888', cursor: 'pointer' }}
            data-testid="admin-tab-pilot">
            <Users size={16} style={{ marginRight: 6, verticalAlign: -3 }} />Founding 100 Pilot
          </button>
          <button onClick={() => { setTab('assessments'); if (attempts.length === 0) loadAttempts(); }}
            style={{ padding: '12px 24px', fontSize: 15, fontWeight: tab === 'assessments' ? 700 : 400, background: 'none', border: 'none',
              borderBottom: tab === 'assessments' ? '3px solid #1E491A' : '3px solid transparent', color: tab === 'assessments' ? '#1E491A' : '#888', cursor: 'pointer' }}
            data-testid="admin-tab-assessments">
            <BarChart3 size={16} style={{ marginRight: 6, verticalAlign: -3 }} />All Assessments
          </button>
        </div>

        {tab === 'pilot' && <PilotCodesPanel />}

        {tab === 'assessments' && (
          <>
        <div className="admin-header">
          <h1>Assessment Attempts</h1>
          <div className="admin-actions">
            <div className="admin-search">
              <Search size={16} />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by email..." data-testid="admin-search" />
            </div>
            <button className="assess-btn-secondary" onClick={loadAttempts} disabled={loading}>
              <RefreshCw size={16} className={loading ? 'spin' : ''} /> Refresh
            </button>
          </div>
        </div>

        <div className="admin-stats">
          <div className="admin-stat">
            <span className="admin-stat-num">{attempts.length}</span>
            <span className="admin-stat-label">Total attempts</span>
          </div>
          <div className="admin-stat">
            <span className="admin-stat-num">{attempts.filter(a => a.status === 'completed').length}</span>
            <span className="admin-stat-label">Completed</span>
          </div>
          <div className="admin-stat">
            <span className="admin-stat-num">{attempts.filter(a => a.status === 'draft').length}</span>
            <span className="admin-stat-label">In progress</span>
          </div>
          <div className="admin-stat">
            <span className="admin-stat-num">{attempts.filter(a => a.chosen_pathway).length}</span>
            <span className="admin-stat-label">Pathway chosen</span>
          </div>
        </div>

        <table className="admin-table" data-testid="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Started</th>
              <th>Status</th>
              <th>Suggested</th>
              <th>Chosen</th>
              <th>Registered</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(a => (
              <React.Fragment key={a.id}>
                <tr className={`admin-row ${expanded === a.id ? 'expanded' : ''}`} onClick={() => loadDetail(a.id)}>
                  <td className="admin-email">{a.email}</td>
                  <td>{a.started_at ? new Date(a.started_at).toLocaleDateString('en-NZ') : '—'}</td>
                  <td>
                    <span className={`admin-status ${a.status}`}>{a.status}</span>
                  </td>
                  <td>
                    {a.suggested_pathway ? (
                      <span className="admin-pathway-tag" style={{ background: PW_COLORS[a.suggested_pathway] }}>
                        {PW_NAMES[a.suggested_pathway]}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    {a.chosen_pathway ? (
                      <span className="admin-pathway-tag" style={{ background: PW_COLORS[a.chosen_pathway] }}>
                        {PW_NAMES[a.chosen_pathway]}
                      </span>
                    ) : '—'}
                  </td>
                  <td>{a.registered ? '✓' : '—'}</td>
                  <td>{expanded === a.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</td>
                </tr>
                {expanded === a.id && detail && (
                  <tr className="admin-detail-row">
                    <td colSpan={7}>
                      <div className="admin-detail" data-testid="admin-detail">
                        <div className="admin-detail-grid">
                          <div className="admin-detail-section">
                            <h4>Scoring</h4>
                            <div className="admin-detail-items">
                              <div><strong>Orientation:</strong> {detail.scoring?.orientation_lean}</div>
                              <div><strong>Drive:</strong> {detail.scoring?.drive_strength}</div>
                              <div><strong>Durability:</strong> {detail.scoring?.durability_read}</div>
                              <div><strong>Skills:</strong> {detail.scoring?.skills_strength_rating}</div>
                              <div><strong>Values:</strong> {detail.scoring?.values_pattern} ({detail.scoring?.values_agreement})</div>
                              <div><strong>Confidence:</strong> {detail.scoring?.confidence_language}</div>
                              <div><strong>Step 1:</strong> {detail.scoring?.step1_outcome}</div>
                              <div><strong>Step 2:</strong> {detail.scoring?.step2_outcome}</div>
                              <div><strong>A signals:</strong> {detail.scoring?.a_signals}</div>
                              <div><strong>C signals:</strong> {detail.scoring?.c_signals}</div>
                              <div><strong>Rule 6:</strong> {detail.scoring?.rule6_fired ? 'FIRED' : 'No'}</div>
                              <div><strong>Close call:</strong> {detail.scoring?.is_close_call ? 'Yes' : 'No'}</div>
                              <div><strong>Distress flag:</strong> {detail.scoring?.distress_flag ? 'Yes' : 'No'}</div>
                            </div>
                          </div>
                          <div className="admin-detail-section">
                            <h4>Financial Profile</h4>
                            {detail.financial_profile?.computed_monthly_income !== undefined ? (
                              <div className="admin-detail-items">
                                <div><strong>Monthly income:</strong> ${detail.financial_profile.computed_monthly_income?.toFixed(2)}</div>
                                <div><strong>Monthly expense:</strong> ${detail.financial_profile.computed_monthly_expense?.toFixed(2)}</div>
                                <div><strong>Net burn:</strong> ${detail.financial_profile.computed_net_monthly_burn?.toFixed(2)}</div>
                                <div><strong>Runway:</strong> {detail.financial_profile.computed_runway_months === 999 ? 'N/A (income covers expenses)' : `${detail.financial_profile.computed_runway_months} months`}</div>
                                <div><strong>Savings:</strong> ${detail.financial_profile.liquid_savings}</div>
                              </div>
                            ) : <p>No financial data submitted</p>}
                          </div>
                        </div>
                        {detail.scoring?.a_reasons?.length > 0 && (
                          <div className="admin-detail-section">
                            <h4>A Reasons</h4>
                            <ul>{detail.scoring.a_reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
                          </div>
                        )}
                        {detail.scoring?.c_reasons?.length > 0 && (
                          <div className="admin-detail-section">
                            <h4>C Reasons</h4>
                            <ul>{detail.scoring.c_reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="admin-empty">No attempts found</td></tr>
            )}
          </tbody>
        </table>
          </>
        )}
      </div>
    </div>
  );
}
